import React, { useState, useEffect, useRef } from 'react';
import { fetchSystemSecurityConfig } from '../services/api';

interface SecurityConfig {
  blockDevTools: boolean;
  blockCopySelection: boolean;
  blockPrintSave: boolean;
  enableWatermark: boolean;
  watermarkOpacity: number;
  enableDebuggerTrap: boolean;
  dashboardFontSize?: string;
  clientIp?: string;
}

interface SecurityProtectionProps {
  userProfile?: {
    email?: string;
    displayName?: string;
  } | null;
}

export const SecurityProtection: React.FC<SecurityProtectionProps> = ({ userProfile }) => {
  const [config, setConfig] = useState<SecurityConfig | null>(null);
  const [isTampered, setIsTampered] = useState(false);
  const [currentTimeStr, setCurrentTimeStr] = useState('');
  const watermarkRef = useRef<HTMLDivElement | null>(null);
  const isUnmountingRef = useRef(false);

  const userEmail = userProfile?.email || 'guest';
  const clientIp = config?.clientIp || 'unknown';

  // 1. Fetch Security Policy Configuration
  useEffect(() => {
    const fetchConfig = async (force: boolean = false) => {
      try {
        const data = await fetchSystemSecurityConfig(force) as SecurityConfig;
        if (data) {
          setConfig(data);
        }
      } catch (error) {
        console.error('Failed to load security settings:', error);
      }
    };

    fetchConfig();
    // Refresh configuration every 5 minutes
    const interval = setInterval(() => fetchConfig(true), 300000);
    return () => clearInterval(interval);
  }, []);

  // Apply Dynamic Dashboard Font Size
  useEffect(() => {
    if (!config || !config.dashboardFontSize) return;

    const styleEl = document.createElement('style');
    styleEl.id = 'mmo-sec-style-font';
    styleEl.innerHTML = `
      html {
        font-size: ${config.dashboardFontSize} !important;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      styleEl.remove();
    };
  }, [config]);

  // Update Time String every 30 seconds for watermark
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const dateStr = now.getFullYear() + '-' + 
                      String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(now.getDate()).padStart(2, '0') + ' ' + 
                      String(now.getHours()).padStart(2, '0') + ':' + 
                      String(now.getMinutes()).padStart(2, '0');
      setCurrentTimeStr(dateStr);
    };

    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  // 2. Apply DevTools, Copy/Selection and Print Blocks
  useEffect(() => {
    if (!config) return;

    const cleanupFns: (() => void)[] = [];

    // DevTools Keydowns & Mouse Click Blockers
    if (config.blockDevTools) {
      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
      };
      
      const handleKeyDownDevTools = (e: KeyboardEvent) => {
        // F12
        if (e.key === 'F12') {
          e.preventDefault();
          return;
        }
        
        // Ctrl+Shift+I / Cmd+Opt+I (Chrome/Safari DevTools)
        // Ctrl+Shift+J / Cmd+Opt+J (Console)
        // Ctrl+U / Cmd+Opt+U (View Source)
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        const modifier = isMac ? e.metaKey && e.altKey : e.ctrlKey && e.shiftKey;
        
        if (modifier && (e.key === 'i' || e.key === 'I' || e.key === 'j' || e.key === 'J')) {
          e.preventDefault();
          return;
        }
        
        if ((isMac ? e.metaKey && e.altKey : e.ctrlKey) && (e.key === 'u' || e.key === 'U')) {
          e.preventDefault();
          return;
        }
      };

      window.addEventListener('contextmenu', handleContextMenu);
      window.addEventListener('keydown', handleKeyDownDevTools);
      
      cleanupFns.push(() => {
        window.removeEventListener('contextmenu', handleContextMenu);
        window.removeEventListener('keydown', handleKeyDownDevTools);
      });
    }

    // Copy / Cut / Selection Event Blockers
    if (config.blockCopySelection) {
      const handleCopyCutSelect = (e: Event) => {
        e.preventDefault();
      };

      window.addEventListener('copy', handleCopyCutSelect);
      window.addEventListener('cut', handleCopyCutSelect);
      window.addEventListener('selectstart', handleCopyCutSelect);
      window.addEventListener('dragstart', handleCopyCutSelect);

      // Inject global user-select styles
      const styleEl = document.createElement('style');
      styleEl.id = 'mmo-sec-style-select';
      styleEl.innerHTML = `
        body, html, #root {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
      `;
      document.head.appendChild(styleEl);

      cleanupFns.push(() => {
        window.removeEventListener('copy', handleCopyCutSelect);
        window.removeEventListener('cut', handleCopyCutSelect);
        window.removeEventListener('selectstart', handleCopyCutSelect);
        window.removeEventListener('dragstart', handleCopyCutSelect);
        styleEl.remove();
      });
    }

    // Print & Page Save Blockers
    if (config.blockPrintSave) {
      const handleKeyDownPrintSave = (e: KeyboardEvent) => {
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        const modifier = isMac ? e.metaKey : e.ctrlKey;

        // Ctrl+P / Cmd+P
        if (modifier && (e.key === 'p' || e.key === 'P')) {
          e.preventDefault();
          alert('보안 정책상 인쇄가 금지되어 있습니다. (Printing is prohibited by security policy.)');
          return;
        }

        // Ctrl+S / Cmd+S
        if (modifier && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          return;
        }
      };

      window.addEventListener('keydown', handleKeyDownPrintSave);

      // Inject print stylesheet to hide components in printer context
      const styleEl = document.createElement('style');
      styleEl.id = 'mmo-sec-style-print';
      styleEl.innerHTML = `
        @media print {
          body, html, #root, #mmo-sec-wmark {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
          }
        }
      `;
      document.head.appendChild(styleEl);

      cleanupFns.push(() => {
        window.removeEventListener('keydown', handleKeyDownPrintSave);
        styleEl.remove();
      });
    }

    return () => {
      cleanupFns.forEach(fn => fn());
    };
  }, [config]);

  // 3. Debugger Trap (DevTools Detection)
  useEffect(() => {
    if (!config || !config.enableDebuggerTrap) return;

    let timer: any = null;

    const runDebuggerTrap = () => {
      if (isUnmountingRef.current) return;

      const startTime = performance.now();
      // eslint-disable-next-line
      debugger;
      const endTime = performance.now();

      // 모바일 기기의 일시적인 GC 또는 초기 렌더링 렉(100ms 내외) 오탐 방지를 위해 임계치를 250ms로 상향하고,
      // 무한 루프로 브라우저를 프리징시키는 대신 변조 감지 UI(isTampered)로 안전하게 상태 전이합니다.
      if (endTime - startTime > 250) {
        setIsTampered(true);
        return;
      }

      timer = setTimeout(runDebuggerTrap, 1500);
    };

    runDebuggerTrap();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [config]);

  // Track component unmounting
  useEffect(() => {
    isUnmountingRef.current = false;
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  // 4. MutationObserver for Watermark Tamper Protection
  useEffect(() => {
    if (!config || !config.enableWatermark || !watermarkRef.current) return;

    const watermarkNode = watermarkRef.current;
    const parentNode = watermarkNode.parentElement || document.body;

    const checkTamper = () => {
      if (isUnmountingRef.current) return;

      // Check if watermark node was removed from DOM
      if (!document.contains(watermarkNode)) {
        setIsTampered(true);
        return;
      }

      // Check computed styles rather than raw style attributes to avoid false positives across browsers
      const computed = window.getComputedStyle(watermarkNode);
      if (
        computed.display === 'none' ||
        computed.visibility === 'hidden' ||
        parseFloat(computed.opacity || '1') < 0.1 ||
        computed.position !== 'fixed'
      ) {
        setIsTampered(true);
      }
    };

    const observer = new MutationObserver((mutations) => {
      if (isUnmountingRef.current) return;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const removedNodes = Array.from(mutation.removedNodes);
          if (removedNodes.includes(watermarkNode)) {
            setIsTampered(true);
            return;
          }
        } else if (mutation.type === 'attributes' && mutation.target === watermarkNode) {
          checkTamper();
        }
      }
    });

    // Observe direct parent for child removal, and watermark node for style/class/id attributes
    observer.observe(parentNode, { childList: true });
    observer.observe(watermarkNode, { attributes: true, attributeFilter: ['style', 'class', 'id'] });

    return () => {
      observer.disconnect();
    };
  }, [config?.enableWatermark]);

  // If tamper detected, completely block/blur the screen
  if (isTampered) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ff4d4d',
        fontFamily: 'sans-serif',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'black', marginBottom: '15px' }}>
          보안 모듈 변조 감지 (Security Module Tampered)
        </h2>
        <p style={{ fontSize: '14px', color: '#ccc', lineHeight: '1.6', maxWidth: '500px' }}>
          금융 거래 안전성 확보를 위해 작동 중인 보안 요소가 훼손되었거나 변조되었습니다.<br />
          정상적인 서비스를 이용하시려면 페이지를 새로고침(F5) 해주십시오.<br />
          <span style={{ fontSize: '12px', opacity: 0.6 }}>
            (Anti-replication security element has been altered. Please reload the page to restore service access.)
          </span>
        </p>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            marginTop: '30px',
            padding: '12px 24px',
            backgroundColor: '#ff4d4d',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          페이지 새로고침 (Reload Page)
        </button>
      </div>
    );
  }

  if (!config || !config.enableWatermark) return null;

  // Generate dynamic watermark background SVG
  const opacity = config.watermarkOpacity;
  const svgText = `${userEmail} | ${clientIp} | ${currentTimeStr} | MMO CONFIDENTIAL`;
  
  // Safe base64 encoding of SVG for data URI to prevent character breaking
  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="350" height="250">
      <text x="175" y="125" fill="rgba(255,255,255,${opacity})" font-size="10" font-family="monospace" font-weight="bold" transform="rotate(-22 175 125)" text-anchor="middle">
        ${svgText}
      </text>
    </svg>
  `;
  const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
  const watermarkBgUrl = `url(data:image/svg+xml;base64,${base64Svg})`;

  return (
    <div
      ref={watermarkRef}
      id="mmo-sec-wmark"
      style={{
        pointerEvents: 'none',
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        backgroundImage: watermarkBgUrl,
        backgroundRepeat: 'repeat',
        opacity: 1,
        display: 'block',
        visibility: 'visible'
      }}
    />
  );
};
